import { AgentThreadRecord } from "@/shared/domain/agentThread";
import { HttpClient, robotoHeaders, PaginatedResponse } from "@/shared/http";
import { bigintReplacer } from "@/shared/json";

import {
  AgentRecord,
  CreateAgentRequest,
  LaunchAgentRequest,
  UpdateAgentRequest,
} from "./AgentRecord";

interface Options {
  abortSignal?: AbortSignal;
  /** Caller-org override, sent as the org header on `REQUIRES_CALLER_ORG` routes. */
  orgId?: string;
}

/**
 * Options for a `REQUIRES_OWNER_ORG` route, which needs the org that *owns*
 * the resource rather than the caller's own. Named apart from
 * {@link Options.orgId} because the two are different headers and mixing them
 * up yields a 403 no frontend test would catch — see `ActionService` and
 * `DeviceService` for the same split.
 */
interface ResourceOwnerOptions {
  abortSignal?: AbortSignal;
  resourceOwnerId?: string;
}

interface ListOptions extends Options {
  pageToken?: string;
  limit?: number;
}

/**
 * Client for the ``/v1/ai/agents`` endpoints.
 *
 * Methods are 1:1 with the backend routes; the React Query hooks in
 * ``./hooks`` wrap each method with caching/invalidation. Keep this layer
 * free of UI state - it only constructs URLs, applies headers, and parses
 * JSON.
 */
export class AgentService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createAgent(
    request: CreateAgentRequest,
    options?: Options,
  ): Promise<AgentRecord> {
    if (!options?.orgId) {
      throw Error("createAgent requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl("v1/ai/agents");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });
    return response.json<AgentRecord>();
  }

  public async getAgent(
    agentId: string,
    options?: Options,
  ): Promise<AgentRecord> {
    // ``orgId`` is required even on the read paths: the agents router is
    // org-scoped, so multi-org users without a declared org get a 400
    // ("single-org operation"). Treat orgId as a hard precondition rather
    // than a "send if available" header to surface the misuse at the call
    // site rather than at the network boundary.
    if (!options?.orgId) {
      throw Error("getAgent requires an Org ID, none was provided");
    }
    const requestUrl = this.#httpClient.constructUrl(`v1/ai/agents/${agentId}`);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });
    return response.json<AgentRecord>();
  }

  /**
   * Fetch an agent by its ``(org_id, name)`` handle.
   *
   * Name + owning org are jointly unique (the ``agents_unique_org_name``
   * constraint), so this addresses at most one agent. Rejects with a
   * `RobotoNotFoundException` (404) when no agent in the org carries that
   * name — callers that distinguish "no such agent" from "the lookup failed"
   * should check for that type rather than catching broadly.
   *
   * Takes `resourceOwnerId`, not the `orgId` every other method here uses:
   * this route is `REQUIRES_OWNER_ORG` (the org can't be derived from a record
   * that hasn't been found yet), so the value travels in the resource-owner
   * header. The distinct parameter name keeps that from being invisible at the
   * call site — `orgId` means "caller org override" throughout the rest of
   * this file, and the two are not interchangeable.
   */
  public async getAgentByName(
    name: string,
    options?: ResourceOwnerOptions,
  ): Promise<AgentRecord> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "getAgentByName requires a resource owner ID, none was provided",
      );
    }
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/agents/name/${encodeURIComponent(name)}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });
    return response.json<AgentRecord>();
  }

  public async listAgents(
    options?: ListOptions,
  ): Promise<PaginatedResponse<AgentRecord>> {
    if (!options?.orgId) {
      throw Error("listAgents requires an Org ID, none was provided");
    }

    const searchParams = new URLSearchParams();
    if (options.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/ai/agents",
      searchParams,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });
    return response.json<PaginatedResponse<AgentRecord>>();
  }

  public async updateAgent(
    agentId: string,
    request: UpdateAgentRequest,
    options?: Options,
  ): Promise<AgentRecord> {
    if (!options?.orgId) {
      throw Error("updateAgent requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/ai/agents/${agentId}`);
    const response = await this.#httpClient.put(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });
    return response.json<AgentRecord>();
  }

  public async deleteAgent(agentId: string, options?: Options): Promise<void> {
    if (!options?.orgId) {
      throw Error("deleteAgent requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/ai/agents/${agentId}`);
    await this.#httpClient.delete(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });
  }

  public async launchAgent(
    agentId: string,
    request: LaunchAgentRequest,
    options?: Options,
  ): Promise<AgentThreadRecord> {
    if (!options?.orgId) {
      throw Error("launchAgent requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/agents/${agentId}/launch`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      // bigintReplacer keeps the bigint analysis-scope bounds full-precision
      // on the wire.
      body: JSON.stringify(request, bigintReplacer),
    });
    return response.json<AgentThreadRecord>();
  }
}
